import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// API 基础地址
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// 创建 axios 实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理 token 过期
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      const isGuest = localStorage.getItem('guest_mode') === 'true';
      
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          });
          
          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // 刷新失败，清除登录状态
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          // 游客模式下不跳转登录页
          if (!isGuest) {
            window.location.href = '/login';
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // 检查本地存储的用户信息
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('access_token');
    const guestMode = localStorage.getItem('guest_mode');
    
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    } else if (guestMode === 'true') {
      setIsGuest(true);
    }
    setLoading(false);
  }, []);

  // 登录
  const login = async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    const { access_token, refresh_token, user } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('guest_mode'); // 清除游客模式
    setUser(user);
    setIsGuest(false);
    
    return user;
  };

  // 邮箱验证码登录
  const loginWithEmail = async (email, verification_code) => {
    const response = await api.post('/api/auth/login-email', { email, verification_code });
    const { access_token, refresh_token, user } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('guest_mode'); // 清除游客模式
    setUser(user);
    setIsGuest(false);
    
    return user;
  };

  // 注册
  const register = async (username, email, password, verification_code) => {
    const response = await api.post('/api/auth/register', {
      username,
      email,
      password,
      verification_code,
    });
    const { access_token, refresh_token, user } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('guest_mode'); // 清除游客模式
    setUser(user);
    setIsGuest(false);
    
    return user;
  };

  // 登出
  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('guest_mode');
      setUser(null);
      setIsGuest(false);
    }
  };

  // 游客模式登录
  const guestLogin = () => {
    localStorage.setItem('guest_mode', 'true');
    setIsGuest(true);
  };

  // 退出游客模式
  const exitGuestMode = () => {
    localStorage.removeItem('guest_mode');
    setIsGuest(false);
  };

  // 发送验证码
  const sendCode = async (email, code_type, username = null) => {
    const response = await api.post('/api/auth/send-code', {
      email,
      code_type,
      username,
    });
    return response.data;
  };

  // 重置密码
  const resetPassword = async (email, verification_code, new_password) => {
    const response = await api.post('/api/auth/reset-password', {
      email,
      verification_code,
      new_password,
    });
    return response.data;
  };

  // 获取当前用户信息
  const getCurrentUser = async () => {
    const response = await api.get('/api/auth/me');
    const user = response.data;
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  // 修改密码
  const changePassword = async (old_password, new_password) => {
    const response = await api.post('/api/auth/change-password', {
      old_password,
      new_password,
    });
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    loginWithEmail,
    register,
    logout,
    sendCode,
    resetPassword,
    getCurrentUser,
    changePassword,
    api,
    isAuthenticated: !!user,
    isGuest,
    guestLogin,
    exitGuestMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
